/**
 * Sprint 23.7 — `<StepIndicator>` for the three-screen `/welcome` flow. A row of
 * dots joined by hairlines; the active step's dot is the primary-text colour,
 * the rest sit at the secondary-border colour. Lives in the step card header,
 * above the per-step "Step N of 3" label.
 */

import { type CSSProperties } from "react";

export interface StepIndicatorProps {
  /** 1-based index of the active step. */
  current: number;
  total: number;
  /** Optional per-step labels; when present, the active label shows alongside. */
  labels?: string[];
}

export function StepIndicator({ current, total, labels }: StepIndicatorProps) {
  return (
    <div style={containerStyle}>
      {Array.from({ length: total }).map((_, i) => {
        const stepNum = i + 1;
        const active = stepNum === current;
        return (
          <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <span
              aria-hidden="true"
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: active ? "var(--color-text-primary)" : "var(--color-border-secondary)",
                flexShrink: 0,
              }}
            />
            {i < total - 1 && <span aria-hidden="true" style={lineStyle} />}
          </span>
        );
      })}
      {labels && labels[current - 1] && (
        <span style={{ marginLeft: 4 }}>{labels[current - 1]}</span>
      )}
    </div>
  );
}

const containerStyle: CSSProperties = {
  padding: "14px 16px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  borderBottom: "0.5px solid var(--color-border-tertiary)",
  fontSize: 10,
  color: "var(--color-text-tertiary)",
  fontFamily: "var(--font-sans)",
};

const lineStyle: CSSProperties = {
  width: 32,
  height: "0.5px",
  background: "var(--color-border-tertiary)",
  flexShrink: 0,
};
